import { Input, type InputProps } from 'antd';
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import type { Uom } from '@/api/generated/model';
import { formatDecimal, parseDecimal, type DecimalString } from '@/lib/decimal';
import { InvalidQty, parseErrorText, type QtyValue } from '@/lib/qty';
import { uomLabel, UOM_PLACES } from '@/lib/units';

const NUMERIC: CSSProperties = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/** Количество в таблице: «1 015 шт», «92,4 кг»; нет значения — «—». */
export function QtyCell({
  value,
  uom,
  maxPlaces,
}: {
  value: DecimalString | null | undefined;
  uom?: Uom | undefined;
  maxPlaces?: number;
}) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <span style={NUMERIC}>—</span>;
  const places = maxPlaces ?? (uom ? UOM_PLACES[uom] : undefined);
  const text = formatDecimal(value, places === undefined ? {} : { maxPlaces: places });
  return <span style={NUMERIC}>{uom ? `${text} ${uomLabel(t, uom)}` : text}</span>;
}

export interface QtyInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'suffix' | 'inputMode'
> {
  value?: QtyValue | undefined;
  onChange?: (value: QtyValue) => void;
  uom: Uom;
  allowNegative?: boolean;
  /** Точность вместо точности единицы: вес штуки — 4 знака (как KgPerPcs в API). */
  places?: number;
  /** Подпись вместо «кг»/«шт»: «кг в 1 шт». */
  unitLabel?: string;
}

function display(value: QtyValue | undefined): string {
  if (value instanceof InvalidQty) return value.text;
  return value ? formatDecimal(value) : '';
}

/**
 * Поле количества. Наружу — строка API ("1234.5") или null, внутри — текст как набран.
 * Точность — по единице учёта: «1,5 шт» не пройдёт, «1,2345 кг» тоже.
 */
export function QtyInput({
  value,
  onChange,
  uom,
  allowNegative = false,
  places,
  unitLabel,
  onBlur,
  ...rest
}: QtyInputProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => display(value));
  // Значение сменили снаружи (сброс формы, подстановка из УПД) — показать его.
  const [shown, setShown] = useState(value);
  if (value !== shown) {
    setShown(value);
    if (!(value instanceof InvalidQty) && parse(text) !== value) setText(display(value));
  }

  function parse(raw: string): QtyValue {
    const r = parseDecimal(raw, { places: places ?? UOM_PLACES[uom], allowNegative });
    return r.ok ? r.value : new InvalidQty(raw, parseErrorText(r.error));
  }

  return (
    <Input
      {...rest}
      value={text}
      inputMode="decimal"
      suffix={unitLabel ?? uomLabel(t, uom)}
      status={value instanceof InvalidQty ? 'error' : (rest.status ?? '')}
      onChange={(e) => {
        const next = parse(e.target.value);
        setText(e.target.value);
        setShown(next);
        onChange?.(next);
      }}
      onBlur={(e) => {
        if (!(value instanceof InvalidQty)) setText(display(value)); // «1234,50» → «1 234,5»
        onBlur?.(e);
      }}
    />
  );
}
